import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Info } from "lucide-react";

// Tier descriptions for info buttons
export const TIER_INFO = {
  BRONZE: {
    name: "Bronze",
    description: "Entry-level tier for small businesses",
    features: [
      "1 active campaign",
      "Basic targeting options",
      "Standard support",
      "Basic analytics"
    ],
    color: "bg-amber-600"
  },
  SILVER: {
    name: "Silver",
    description: "Growing businesses with moderate ad spend",
    features: [
      "Up to 5 active campaigns",
      "Advanced targeting (time, demographics)",
      "Priority support",
      "Enhanced analytics with exports",
      "Deal/offer capabilities"
    ],
    color: "bg-gray-400"
  },
  GOLD: {
    name: "Gold",
    description: "Premium tier for high-volume advertisers",
    features: [
      "Unlimited campaigns",
      "Full targeting suite including competitor redirection",
      "Dedicated account manager",
      "Real-time analytics & API access",
      "Custom integrations",
      "Priority ad placement"
    ],
    color: "bg-yellow-500"
  }
};

// CPDC metric explanations
export const CPDC_INFO = {
  title: "Delivered Customers",
  description: "You're charged when a passenger arrives at or near your business based on the outcomes below.",
  types: [
    {
      name: "Direct",
      description: "Passenger saw or engaged with your ad and was dropped off directly at your business (within 50m)."
    },
    {
      name: "Proximity",
      description: "Passenger saw or engaged with your ad and was then dropped off near your business (within 50–200m)."
    },
    {
      name: "Prompted",
      description: "Passenger was guided through Booma's AI-driven recommendations or in-app prompts, resulting in being dropped off at your business."
    }
  ],
  footer: "Distances and attribution windows are designed to reflect real customer intent and maximise meaningful outcomes for your business."
};

// Reusable clickable info button component
export const InfoButton = ({ content, className = "" }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button className={`ml-1 text-muted-foreground hover:text-foreground transition-colors ${className}`}>
        <Info className="h-4 w-4" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="max-w-xs">
      <p className="text-sm">{content}</p>
    </PopoverContent>
  </Popover>
);

// Tier info badge with popover
export const TierInfoBadge = ({ tier }) => {
  const info = TIER_INFO[tier] || TIER_INFO.BRONZE;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="ml-1 text-muted-foreground hover:text-foreground transition-colors cursor-help">
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="max-w-sm" side="bottom" sideOffset={5}>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${info.color}`} />
            <span className="font-bold">{info.name} Tier</span>
          </div>
          <p className="text-sm text-muted-foreground">{info.description}</p>
          <ul className="text-xs space-y-1 mt-2">
            {info.features.map((feature, i) => (
              <li key={i} className="flex items-center gap-1">
                <span className="text-green-500">✓</span> {feature}
              </li>
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// CPDC detailed info component
export const CPDCInfoButton = () => (
  <Popover>
    <PopoverTrigger asChild>
      <button className="ml-1 text-muted-foreground hover:text-foreground transition-colors">
        <Info className="h-4 w-4" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="max-w-md">
      <div className="space-y-3">
        <div>
          <span className="font-bold">{CPDC_INFO.title}</span>
          <p className="text-xs text-muted-foreground mt-1">{CPDC_INFO.description}</p>
        </div>
        <div className="space-y-2">
          {CPDC_INFO.types.map((type, i) => (
            <div key={i} className="p-2 rounded bg-secondary/50">
              <span className="font-medium text-sm">{type.name}</span>
              <p className="text-xs text-muted-foreground">{type.description}</p>
            </div>
          ))}
        </div>
        {CPDC_INFO.footer && (
          <p className="text-xs text-muted-foreground italic mt-2 pt-2 border-t border-border">{CPDC_INFO.footer}</p>
        )}
      </div>
    </PopoverContent>
  </Popover>
);

// Metric terminology mapping
export const METRIC_LABELS = {
  CPM: "Views",
  CPC: "Clicks",
  CPDC: "Arrivals",
  CPDC_DIRECT: "Arrivals Direct",
  CPDC_PROXIMITY: "Arrivals Proximity",
  CPDC_PROMPTED: "Arrivals Prompted"
};

export const getMetricLabel = (metricType) => {
  return METRIC_LABELS[metricType] || metricType;
};
